import { createContext, ReactNode, useContext } from "react";

import { usePortalHomeLink } from "@/hooks/usePortalHomeLink";
import {
  useShowFriendsWithBenefitsAttentionBubble,
  useVisibleNavigationItems,
} from "@/utils/navigation/navigationItems";

export type PortalNavigationItem = {
  icon: JSX.Element;
  activeIcon: JSX.Element;
  text: string;
  target: string;
  ariaLabel?: string;
  bottomNavText?: string;
};

export type PortalShellConfigValue = {
  homeLink: string;
  navigationItems: readonly PortalNavigationItem[];
  showCockpitTodos?: boolean;
  showFriendsWithBenefitsBubble?: boolean;
};

const PortalShellConfigContext = createContext<PortalShellConfigValue | null>(
  null,
);

export const PortalShellProvider = ({
  value,
  children,
}: {
  value: PortalShellConfigValue;
  children: ReactNode;
}) => (
  <PortalShellConfigContext.Provider value={value}>
    {children}
  </PortalShellConfigContext.Provider>
);

/**
 * Returns portal shell navigation config. When wrapped in PortalShellProvider
 * (partner portal), uses the override value. Otherwise falls back to customer
 * defaults (visible nav items, portal home link, todos + FwB bubble enabled).
 */
export const usePortalShellConfig = () => {
  const override = useContext(PortalShellConfigContext);
  const useCustomerDefaults = override === null;
  const defaultHomeLink = usePortalHomeLink();
  const defaultNavigationItems = useVisibleNavigationItems({
    enabled: useCustomerDefaults,
  });
  const defaultFriendsWithBenefitsBubble =
    useShowFriendsWithBenefitsAttentionBubble({
      enabled: useCustomerDefaults,
    });

  if (override) {
    return {
      homeLink: override.homeLink,
      navigationItems: override.navigationItems,
      showCockpitTodos: override.showCockpitTodos ?? false,
      showFriendsWithBenefitsAttentionBubble:
        override.showFriendsWithBenefitsBubble ?? false,
    };
  }

  return {
    homeLink: defaultHomeLink,
    navigationItems: defaultNavigationItems as PortalNavigationItem[],
    showCockpitTodos: true,
    showFriendsWithBenefitsAttentionBubble: defaultFriendsWithBenefitsBubble,
  };
};
